"use client";

import * as React from "react";
import { Eraser } from "lucide-react";
import { Button } from "./button";
import { cn } from "@repo/utils";

interface SignatureInputProps {
  value?: string | null;
  onChange?: (signature: string | null) => void;
  canvasRef?: React.RefObject<HTMLCanvasElement>;
  className?: string;
  disabled?: boolean;
}

const SignatureInput = React.forwardRef<HTMLCanvasElement, SignatureInputProps>(
  ({ value, onChange, canvasRef: externalCanvasRef, className, disabled = false }, ref) => {
    const internalCanvasRef = React.useRef<HTMLCanvasElement>(null);
    const canvasRef = externalCanvasRef || internalCanvasRef;

    const [isDrawing, setIsDrawing] = React.useState(false);
    const [hasSignature, setHasSignature] = React.useState(false);
    const [strokeColor, setStrokeColor] = React.useState("#000000");

    React.useImperativeHandle(ref, () => canvasRef.current as HTMLCanvasElement);

    // Detect dark mode
    React.useEffect(() => {
      const updateStrokeColor = () => {
        const isDark =
          document.documentElement.classList.contains("dark") ||
          window.matchMedia("(prefers-color-scheme: dark)").matches;
        setStrokeColor(isDark ? "#ffffff" : "#000000");
      };

      updateStrokeColor();

      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      mediaQuery.addEventListener("change", updateStrokeColor);

      const observer = new MutationObserver(updateStrokeColor);
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["class"],
      });

      return () => {
        mediaQuery.removeEventListener("change", updateStrokeColor);
        observer.disconnect();
      };
    }, []);

    // Load existing signature
    React.useEffect(() => {
      if (value && canvasRef.current) {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          const img = new Image();
          img.onload = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0);
            setHasSignature(true);
          };
          img.src = value;
        }
      }
    }, [value, canvasRef]);

    const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
      if (disabled) return;
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      setIsDrawing(true);
      setHasSignature(true);

      const rect = canvas.getBoundingClientRect();
      const x = "touches" in e ? e.touches[0].clientX - rect.left : e.clientX - rect.left;
      const y = "touches" in e ? e.touches[0].clientY - rect.top : e.clientY - rect.top;

      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    };

    const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
      if (!isDrawing || disabled) return;
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const rect = canvas.getBoundingClientRect();
      const x = "touches" in e ? e.touches[0].clientX - rect.left : e.clientX - rect.left;
      const y = "touches" in e ? e.touches[0].clientY - rect.top : e.clientY - rect.top;

      ctx.lineTo(x, y);
      ctx.stroke();
    };

    const stopDrawing = () => {
      if (!isDrawing) return;
      setIsDrawing(false);

      const canvas = canvasRef.current;
      if (canvas && onChange) {
        const dataUrl = canvas.toDataURL("image/png");
        onChange(dataUrl);
      }
    };

    const clearSignature = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      setHasSignature(false);
      onChange?.(null);
    };

    const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
      e.preventDefault();
      startDrawing(e);
    };

    const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
      e.preventDefault();
      draw(e);
    };

    return (
      <div className={cn("relative", className)}>
        <canvas
          ref={canvasRef}
          width={400}
          height={200}
          className={cn(
            "w-full max-w-[400px] h-[200px] border border-input rounded-md bg-background touch-none",
            disabled && "opacity-50 cursor-not-allowed"
          )}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={stopDrawing}
        />
        {hasSignature && !disabled && (
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            className="absolute top-2 right-2"
            onClick={clearSignature}
          >
            <Eraser className="size-4" />
          </Button>
        )}
      </div>
    );
  }
);

SignatureInput.displayName = "SignatureInput";

export { SignatureInput };
